import { StylesConfig } from 'react-select';

import colors from '@constants/colors';

type TPrepareStyles = (
	_isError: boolean | undefined,
	_customWidth: string | undefined,
	_customMinWidth: string | undefined,
	_customMaxWidth: string | undefined,
	_isFilterStyle: boolean,
	_isPagination: boolean,
	_isEditable: boolean,
	_isRenamable: boolean,
	_isFavorite: boolean,
	_isMulti?: boolean,
	_isReadOnly?: boolean,
	_isActionsDropdown?: boolean,
	_minHeight?: string,
	_isWithoutBorder?: boolean
) => StylesConfig;

const PrepareSelectStyles: TPrepareStyles = (
	isError,
	customWidth,
	customMinWidth,
	customMaxWidth,
	isFilterStyle,
	isPagination,
	isEditable,
	isRenamable,
	isFavorite,
	isMulti,
	isReadOnly,
	isActionsDropdown,
	minHeight,
	isWithoutBorder,
) => ({
	control: (base, state) => {
		const backgroundColor = state.isDisabled
			? colors.gray10
			: isFilterStyle && state.hasValue
				? colors.indigo50
				: colors.white;
		const border = state.isDisabled
			? `1px solid ${colors.gray10}`
			: isError
				? `1px solid ${colors.pink10}`
				: state.isFocused
					? `1px solid ${colors.indigo50}`
					: `1px solid ${colors.indigo10}`;
		const width = customWidth || 'auto';
		const minWidth = customMinWidth || 'auto';
		const maxWidth = customMaxWidth || 'auto';

		return {
			...base,
			width,
			minWidth,
			maxWidth,
			backgroundColor,
			borderRadius: '8px',
			border: isWithoutBorder ? 'none' : border,
			minHeight: minHeight || '40px',
			boxShadow: 'none',
			padding: '0',
			'&:hover': {
				border: isWithoutBorder ? 'none' : border,
			},
		};
	},
	indicatorSeparator: () => ({
		display: 'none',
	}),
	group: (base) => ({
		...base,
		padding: '0',
	}),
	groupHeading: () => ({
		color: colors.indigo70,
		textTransform: 'uppercase',
		padding: '8px 16px',
		background: colors.indigo05,
	}),
	singleValue: (base, state) => {
		const color = isReadOnly
			? colors.gray70
			: state.isDisabled
				? colors.gray40
				: isFilterStyle && state.hasValue
					? colors.white
					: colors.indigo70;

		return {
			...base,
			fontFamily: 'Inter',
			fontStyle: 'normal',
			fontWeight: 'normal',
			fontSize: isPagination ? '12px' : '16px',
			lineHeight: isPagination ? '18px' : '24px',
			color: isError ? colors.pink50 : color,
			paddingLeft: '2px',
			paddingTop: '0',
			margin: 0,
		};
	},
	input: (base) => ({
		...base,
		position: 'relative',
		top: undefined,
	}),
	indicatorsContainer: (base, state) =>
		isFilterStyle && state.hasValue
			? {
				display: 'flex',
				width: '30px',

				'>div:first-of-type': {
					left: '0',

					'.close': {
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					},
				},

				'>div:last-child': {
					display: 'none',
				},
			}
			: { ...base },
	clearIndicator: (base, state) => {
		const background = isFilterStyle && state.hasValue ? colors.indigo50 : colors.white;

		return {
			...base,
			position: 'relative',
			left: '30px',
			background,
			borderRadius: '4px',
			marginRight: '5px',
			paddingRight: '2px',

			':hover': {
				cursor: 'pointer',
			},
		};
	},
	placeholder: (base, state) => {
		const color = isReadOnly
			? colors.gray70
			: state.isDisabled
				? colors.gray40
				: isError
					? colors.pink50
					: colors.indigo20;

		return {
			...base,
			color,
			whiteSpace: 'nowrap',
		};
	},
	menu: (base) => ({
		...base,
		margin: '5px 0 0 1px',
		zIndex: 4,
	}),
	menuList: (base) => ({
		...base,
		scrollbarWidth: 'none',
	}),
	option: (base, state) => {
		const backgroundColor = state.isDisabled && !(state as any)?.data?.isPlaceholder
			? colors.gray10
			: state.isSelected ? colors.indigo60 : colors.white;
		const hoverBackgroundColor = state.isDisabled
			? undefined
			: isMulti
				? colors.indigo10
				: state.isSelected ? backgroundColor : colors.indigo10;
		const color = state.isDisabled
			? colors.gray40
			: state.isSelected && !isMulti ? colors.white : colors.indigo70;
		const customColor = (state as any)?.data?.color;
		const customHoverColor = (state as any)?.data?.hoverColor;
		const paddingRight = isActionsDropdown
			? '30px'
			: isRenamable && isEditable
				? '95px'
				: isRenamable || isEditable
					? '65px'
					: isFavorite
						? '30px' : '6px';
		const textOverflow = 'ellipsis';
		const overflow = 'hidden';
		const whiteSpace = 'nowrap';
		const padding = isMulti ? '8px 16px' : '5px 15px';
		const fontSize = isMulti ? '14px' : isPagination ? '12px' : '16px';
		const lineHeight = isMulti ? '20px' : isPagination ? '18px' : '24px';
		const isFixedMultiValue = isMulti && (state as any)?.data?.isFixed;
		const isSelected = state.isSelected && !isFixedMultiValue;

		return {
			...base,
			backgroundColor: isMulti ? colors.white : backgroundColor,
			color: customColor || color,
			cursor: state.isDisabled ? 'not-allowed' : 'pointer',
			fontFamily: 'Inter, sans-serif',
			fontSize,
			fontWeight: 400,
			lineHeight,
			overflow,
			padding,
			paddingRight,
			textOverflow,
			transition: '200ms',
			whiteSpace,

			'&:hover': {
				background: isFixedMultiValue ? colors.white : hoverBackgroundColor,
				color: state.isDisabled
					? undefined
					: isSelected && !isMulti
						? colors.white
						: isFixedMultiValue
							? colors.indigo70
							: (customHoverColor || colors.indigo70),
			},
		};
	},
	multiValue: (base, state) => {
		const backgroundColor = state.isDisabled || (state?.data as any)?.isFixed ? colors.gray10 : colors.indigo10;

		return {
			...base,
			borderRadius: '12px',
			backgroundColor,
			padding: '4px 10px',
			boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
		};
	},
	multiValueLabel: (base) => ({
		...base,
		color: colors.indigo70,
	}),
	multiValueRemove: (base, state) => {
		const color = state.isDisabled || (state?.data as any)?.isFixed ? colors.gray40 : colors.indigo70;

		return {
			...base,
			color,
			backgroundColor: colors.gray05,
			borderRadius: '50%',
			marginLeft: '5px',

			':hover': {
				cursor: 'pointer',
			},
		};
	},
});

export default PrepareSelectStyles;
